import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  useApproveRegistrationRequestMutation,
  useGetRegistrationRequestsQuery,
  useRejectRegistrationRequestMutation,
} from "@/store/admin_api";
import { RegistrationRequest } from "@/model/interface/RegistrationRequest";
import { RegistrationRequestStatus } from "@/enum/RegistrationRequestStatus";
import { apiErrorMessage } from "@/util/apiError";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FaCheck, FaEnvelope, FaLink, FaTimes } from "react-icons/fa";
import { toast } from "sonner";

const STATUS_TABS = [
  { value: "ALL", label: "All" },
  { value: RegistrationRequestStatus.PENDING, label: "Pending" },
  { value: RegistrationRequestStatus.APPROVED, label: "Approved" },
  { value: RegistrationRequestStatus.REGISTERED, label: "Registered" },
  { value: RegistrationRequestStatus.REJECTED, label: "Rejected" },
];

const STATUS_BADGE_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  PENDING: "outline",
  APPROVED: "default",
  REGISTERED: "secondary",
  REJECTED: "destructive",
};

const RISK_FLAG_LABELS: Record<string, string> = {
  FREE_EMAIL: "Free email",
  ALIAS_EMAIL: "Alias",
  IP_VELOCITY: "IP velocity",
  DUP_NORMALIZED: "Dup email",
};

const HIGH_RISK_FLAGS = new Set(["IP_VELOCITY", "DUP_NORMALIZED"]);

export default function AccessRequestsList() {
  const { data: requests, isLoading } = useGetRegistrationRequestsQuery();
  const [approveRequest] = useApproveRegistrationRequestMutation();
  const [rejectRequest] = useRejectRegistrationRequestMutation();
  const [statusTab, setStatusTab] = useState("ALL");
  const [busyRequestId, setBusyRequestId] = useState<string | null>(null);

  const filteredRequests =
    statusTab === "ALL" ? requests : requests?.filter((request) => request.status === statusTab);

  const requesterName = (request: RegistrationRequest) => {
    const name = [request.firstName, request.lastName].filter(Boolean).join(" ");
    return name || "-";
  };

  const inviteLink = (request: RegistrationRequest) => {
    return `${window.location.origin}/register?invite=${request.inviteToken}`;
  };

  const riskFlags = (request: RegistrationRequest) => {
    return request.riskFlags?.split(",").map((flag) => flag.trim()).filter(Boolean) ?? [];
  };

  const riskTooltip = (request: RegistrationRequest) => {
    return `IP ${request.ipAddress || "unknown"}, score ${request.riskScore ?? 0}`;
  };

  const handleApprove = async (request: RegistrationRequest, resend: boolean) => {
    setBusyRequestId(request.id);
    try {
      await approveRequest(request.id).unwrap();
      toast.success(
        resend
          ? `A fresh invite was emailed to ${request.email}`
          : `${request.email} approved. The invite email is on its way.`,
      );
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to approve the request"));
    } finally {
      setBusyRequestId(null);
    }
  };

  const handleReject = async (request: RegistrationRequest) => {
    setBusyRequestId(request.id);
    try {
      await rejectRequest(request.id).unwrap();
      toast.success(`${request.email} rejected`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to reject the request"));
    } finally {
      setBusyRequestId(null);
    }
  };

  const handleCopyInviteLink = async (request: RegistrationRequest) => {
    await navigator.clipboard.writeText(inviteLink(request));
    toast.success("Invite link copied to clipboard");
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Access Requests</h2>
        <p className="text-muted-foreground text-sm">
          Approve a request to email the person a single-use invite link.
        </p>
      </div>

      <Tabs value={statusTab} onValueChange={setStatusTab}>
        <TabsList>
          {STATUS_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Referred by</TableHead>
            <TableHead>Risk</TableHead>
            <TableHead>Message</TableHead>
            <TableHead>Requested</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!filteredRequests?.length && (
            <TableRow>
              <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                No access requests
              </TableCell>
            </TableRow>
          )}
          {filteredRequests?.map((request) => {
            const busy = busyRequestId === request.id;
            return (
              <TableRow key={request.id}>
                <TableCell className="font-medium">{requesterName(request)}</TableCell>
                <TableCell>{request.email}</TableCell>
                <TableCell>{request.company || "-"}</TableCell>
                <TableCell>
                  {request.referredByOrganisationId ? (
                    <Link
                      to={`/platform-admin/organisations/${request.referredByOrganisationId}`}
                      className="font-medium hover:text-primary"
                    >
                      {request.referredByOrganisationName}
                    </Link>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell title={riskTooltip(request)}>
                  {riskFlags(request).length ? (
                    <span className="flex flex-wrap gap-1">
                      {riskFlags(request).map((flag) => (
                        <Badge
                          key={flag}
                          variant={HIGH_RISK_FLAGS.has(flag) ? "destructive" : "secondary"}
                        >
                          {RISK_FLAG_LABELS[flag] || flag}
                        </Badge>
                      ))}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="max-w-xs truncate" title={request.message}>
                  {request.message || "-"}
                </TableCell>
                <TableCell>
                  {request.createdAt ? new Date(request.createdAt).toLocaleDateString() : "-"}
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_BADGE_VARIANTS[request.status] || "outline"}>
                    {request.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right space-x-2 whitespace-nowrap">
                  {(request.status === RegistrationRequestStatus.PENDING ||
                    request.status === RegistrationRequestStatus.REJECTED) && (
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => handleApprove(request, false)}
                    >
                      <FaCheck className="mr-1 h-3 w-3" />
                      Approve
                    </Button>
                  )}
                  {request.status === RegistrationRequestStatus.APPROVED && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopyInviteLink(request)}
                      >
                        <FaLink className="mr-1 h-3 w-3" />
                        Copy link
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => handleApprove(request, true)}
                      >
                        <FaEnvelope className="mr-1 h-3 w-3" />
                        Resend
                      </Button>
                    </>
                  )}
                  {request.status !== RegistrationRequestStatus.REJECTED &&
                    request.status !== RegistrationRequestStatus.REGISTERED && (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={busy}
                        onClick={() => handleReject(request)}
                      >
                        <FaTimes className="mr-1 h-3 w-3" />
                        Reject
                      </Button>
                    )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
